import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import BrushOutlinedIcon from '@mui/icons-material/BrushOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import PrecisionManufacturingOutlinedIcon from '@mui/icons-material/PrecisionManufacturingOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import HeroVisual from '../../components/HeroVisual';
import ScrollProgressBar from '../../components/ScrollProgressBar';
import MagneticButton from '../../components/MagneticButton';
import TiltCard from '../../components/TiltCard';
import ParallaxImage from '../../components/ParallaxImage';
import { Reveal, RevealGroup, RevealItem } from '../../components/Reveal';
import '../css/Homepage.css';

const Homepage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const toggleFaq = (index) => setOpenFaq(openFaq === index ? null : index);

  const stats = [
    { value: '80+', label: 'Countries Reached' },
    { value: '20+', label: 'Years of Export' },
    { value: '500+', label: 'Products Available' },
    { value: '10K+', label: 'Orders Fulfilled' },
    { value: 'ISO 9001', label: 'Certified Quality' },
  ];

  const productCategories = [
    {
      icon: <BrushOutlinedIcon />,
      title: 'Paint Application Tools',
      description: 'Professional brushes, rollers, and spray equipment for perfect finishes.',
      link: '/categories',
    },
    {
      icon: <BuildOutlinedIcon />,
      title: 'Surface Preparation Tools',
      description: 'Scrapers, sanders, and putty knives for optimal surface preparation.',
      link: '/categories',
    },
    {
      icon: <SettingsOutlinedIcon />,
      title: 'Sanding & Polishing Tools',
      description: 'High-quality grinding and polishing tools for smooth finishes.',
      link: '/categories',
    },
    {
      icon: <Inventory2OutlinedIcon />,
      title: 'Accessories & Assortments',
      description: 'Trays, covers, grids, and complete accessory kits.',
      link: '/categories',
    },
  ];

  const whyPoints = [
    {
      title: 'International Shipping Expertise',
      description: 'With over 20 years of global export experience, we ensure timely delivery with full documentation to any market.',
    },
    {
      title: 'Manufacturing Quality & Consistency',
      description: 'State-of-the-art facilities and rigorous testing ensure batch-to-batch consistency that your customers rely on.',
    },
    {
      title: 'Complete Product Ecosystem',
      description: 'Source everything from one trusted manufacturer. Simplify your supply chain with quality-matched products.',
    },
    {
      title: 'Bulk & Export Ready',
      description: 'Our production capacity and logistics are built for export — consistent availability at competitive pricing.',
    },
    {
      title: 'Professional-Grade Products',
      description: 'Engineered for demanding applications, offering superior durability over consumer-grade alternatives.',
    },
  ];

  const engineeringPoints = [
    {
      num: '01',
      icon: <SearchOutlinedIcon />,
      title: 'Premium Material Selection',
      description: 'We source only the highest-grade materials from certified suppliers to ensure lasting product quality.',
    },
    {
      num: '02',
      icon: <PrecisionManufacturingOutlinedIcon />,
      title: 'Precision Manufacturing',
      description: 'Advanced production techniques ensure consistent quality in every single product we ship.',
    },
    {
      num: '03',
      icon: <VerifiedOutlinedIcon />,
      title: 'Rigorous Quality Testing',
      description: 'Every batch undergoes extensive testing before shipment to maintain our global quality standards.',
    },
  ];

  const faqs = [
    { question: 'What are your minimum order quantities?', answer: 'Our minimum order quantities vary by product category. Please contact us for specific MOQ details tailored to your needs.' },
    { question: 'How long does international shipping take?', answer: 'International shipping typically takes 5–15 days depending on the destination country and shipping method chosen.' },
    { question: 'Do you offer product customization?', answer: 'Yes, we offer customization options including branding and packaging for bulk orders. Contact us to discuss your requirements.' },
    { question: 'What quality certifications do you hold?', answer: 'We hold ISO 9001:2015, ISO 14001:2015, and various international quality certifications relevant to our product lines.' },
    { question: 'What payment terms do you offer?', answer: 'We offer flexible payment terms including L/C, T/T, and other mutually agreed methods for international trade.' },
  ];

  return (
    <div className="homepage">
      <ScrollProgressBar />

      {/* ── HERO ── */}
      <section className="hero-section">
        <HeroVisual />
        <div className="container hero-container">
          <Reveal as="div" className="hero-inner">
            <span className="eyebrow">Trusted in 80+ countries</span>
            <h1 className="hero-headline">
              Painting tools engineered<br />
              for <span className="accent-text">global scale.</span>
            </h1>
            <p className="hero-sub">
              Two decades supplying leading distributors across continents — consistent quality,
              competitive pricing, dependable export logistics.
            </p>
            <div className="hero-actions">
              <MagneticButton to="/contact" className="btn-primary">
                Request a Quote <ArrowForwardIcon className="btn-icon" />
              </MagneticButton>
              <Link to="/products" className="btn-ghost">Explore Products</Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── MARQUEE PROOF STRIP ── */}
      <div className="marquee-strip">
        <div className="marquee-track">
          {[...stats, ...stats].map((s, i) => (
            <span className="marquee-item" key={i}>
              <strong>{s.value}</strong> {s.label}
            </span>
          ))}
        </div>
      </div>

      {/* ── CATEGORIES ── */}
      <section className="categories-section">
        <div className="container">
          <Reveal className="section-head">
            <span className="eyebrow">What We Make</span>
            <h2 className="section-title">A complete ecosystem, one source.</h2>
          </Reveal>
          <RevealGroup className="categories-grid">
            {productCategories.map((cat, i) => (
              <RevealItem key={i}>
                <TiltCard as={Link} to={cat.link} className="glass-card">
                  <div className="glass-card-icon">{cat.icon}</div>
                  <h3>{cat.title}</h3>
                  <p>{cat.description}</p>
                  <span className="glass-card-cta">Explore <ArrowForwardIcon /></span>
                </TiltCard>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      {/* ── WHY US ── */}
      <section className="why-section">
        <div className="container">
          <div className="why-inner">
            <Reveal as="div" className="why-left">
              <span className="eyebrow">Why Choose Us</span>
              <h2 className="section-title">Why distributors stay.</h2>
              <p className="why-intro">We've built partnerships across 80+ countries by consistently delivering on our promises.</p>
              <RevealGroup className="why-list">
                {whyPoints.map((pt, i) => (
                  <RevealItem key={i} className="why-item">
                    <div className="why-num">{String(i + 1).padStart(2, '0')}</div>
                    <div>
                      <h4>{pt.title}</h4>
                      <p>{pt.description}</p>
                    </div>
                  </RevealItem>
                ))}
              </RevealGroup>
            </Reveal>
            <Reveal as="div" className="why-right" delay={0.15}>
              <div className="why-img-stack">
                <ParallaxImage src="/images/homepage/hero-section-4.png" alt="Facility" className="why-img-main" range={40} />
                <ParallaxImage src="/images/homepage/hero-section-5.png" alt="Team" className="why-img-sm" range={20} />
                <div className="why-badge">
                  <span className="why-badge-num">20+</span>
                  <span className="why-badge-label">Years of Export Experience</span>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── ENGINEERING PIPELINE ── */}
      <section className="engineering-section">
        <div className="container">
          <Reveal className="section-head">
            <span className="eyebrow">Our Process</span>
            <h2 className="section-title">Every tool follows the same pipeline.</h2>
          </Reveal>
          <RevealGroup className="pipeline">
            {engineeringPoints.map((pt, i) => (
              <RevealItem key={i} className="pipeline-step">
                <span className="pipeline-num">{pt.num}</span>
                <div className="pipeline-icon">{pt.icon}</div>
                <h3>{pt.title}</h3>
                <p>{pt.description}</p>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      {/* ── TESTIMONIAL ── */}
      <Reveal as="section" className="testimonial-section">
        <div className="container">
          <div className="testimonial-card glass-card">
            <FormatQuoteIcon className="quote-icon" />
            <p className="testimonial-quote">
              "Quality is impressive for the price point. Done multiple orders and quality control is notably strong
              with minimal complaints from our retail network. A good, dependable partner — highly recommend."
            </p>
            <div className="testimonial-author">
              <div className="author-avatar">MR</div>
              <div>
                <strong>Marcus Rodriguez</strong>
                <span>Importer, Mexico</span>
              </div>
              <div className="testimonial-stars">★★★★★</div>
            </div>
          </div>
        </div>
      </Reveal>

      {/* ── CTA BANNER ── */}
      <Reveal as="section" className="cta-banner">
        <div className="container">
          <h2 className="section-title">Ready to source professional tools?</h2>
          <p>Get a tailored quote for your market. Our export team responds within 24 hours.</p>
          <MagneticButton to="/contact" className="btn-primary">Request a Quote</MagneticButton>
        </div>
      </Reveal>

      {/* ── FAQ ── */}
      <section className="faq-section">
        <div className="container">
          <Reveal className="section-head">
            <span className="eyebrow">FAQ</span>
            <h2 className="section-title">Common questions.</h2>
          </Reveal>
          <RevealGroup className="faq-list">
            {faqs.map((faq, i) => (
              <RevealItem key={i} className={`faq-item ${openFaq === i ? 'active' : ''}`}>
                <button className="faq-q" onClick={() => toggleFaq(i)}>
                  <span>{faq.question}</span>
                  {openFaq === i ? <RemoveIcon /> : <AddIcon />}
                </button>
                {openFaq === i && (
                  <div className="faq-a">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

    </div>
  );
};

export default Homepage;
